/**
 * Format file size in bytes to human readable format
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format duration in milliseconds to human readable format
 */
export function formatDuration(milliseconds) {
  if (milliseconds <= 0) return 'Expired';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h ${minutes % 60}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Format date to relative time (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelativeTime(date) {
  const now = new Date();
  const targetDate = new Date(date);
  const diffInSeconds = Math.floor((targetDate - now) / 1000);
  
  if (diffInSeconds < 0) {
    // Past time
    const absDiff = Math.abs(diffInSeconds);
    if (absDiff < 60) return 'just now';
    if (absDiff < 3600) return `${Math.floor(absDiff / 60)} minutes ago`;
    if (absDiff < 86400) return `${Math.floor(absDiff / 3600)} hours ago`;
    if (absDiff < 2592000) return `${Math.floor(absDiff / 86400)} days ago`;
    return targetDate.toLocaleDateString();
  } else {
    // Future time
    if (diffInSeconds < 60) return 'in less than a minute';
    if (diffInSeconds < 3600) return `in ${Math.floor(diffInSeconds / 60)} minutes`;
    if (diffInSeconds < 86400) return `in ${Math.floor(diffInSeconds / 3600)} hours`;
    if (diffInSeconds < 2592000) return `in ${Math.floor(diffInSeconds / 86400)} days`;
    return targetDate.toLocaleDateString();
  }
}

/**
 * Format date to a readable string
 */
export function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format number with commas
 */
export function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Get file icon based on mime type
 */
export function getFileIcon(mimeType) {
  // Handle undefined/null mimeType
  if (!mimeType || typeof mimeType !== 'string') {
    return '📄'; // default file icon
  }
  
  const type = mimeType.toLowerCase();
  
  if (type.startsWith('image/')) {
    return '🖼️';
  } else if (type.startsWith('video/')) {
    return '🎥';
  } else if (type.startsWith('audio/')) {
    return '🎵';
  } else if (type.includes('pdf')) {
    return '📄';
  } else if (type.includes('word') || type.includes('document')) {
    return '📝';
  } else if (type.includes('excel') || type.includes('sheet')) {
    return '📊';
  } else if (type.includes('powerpoint') || type.includes('presentation')) {
    return '📊';
  } else if (type.includes('zip') || type.includes('archive') || type.includes('compressed')) {
    return '📦';
  } else if (type.includes('text') || type.includes('plain')) {
    return '📄';
  } else if (type.includes('json') || type.includes('javascript') || type.includes('html') || type.includes('css')) {
    return '💻';
  } else {
    return '📄';
  }
}

/**
 * Get file type category
 */
export function getFileCategory(mimeType) {
  // Handle undefined/null mimeType
  if (!mimeType || typeof mimeType !== 'string') {
    return 'File'; // default category
  }
  
  const type = mimeType.toLowerCase();
  
  if (type.startsWith('image/')) return 'Image';
  if (type.startsWith('video/')) return 'Video';
  if (type.startsWith('audio/')) return 'Audio';
  if (type.includes('pdf')) return 'PDF';
  if (type.includes('word') || type.includes('document')) return 'Document';
  if (type.includes('excel') || type.includes('sheet')) return 'Spreadsheet';
  if (type.includes('powerpoint') || type.includes('presentation')) return 'Presentation';
  if (type.includes('zip') || type.includes('archive') || type.includes('compressed')) return 'Archive';
  if (type.includes('text') || type.includes('plain')) return 'Text';
  if (type.includes('json') || type.includes('javascript') || type.includes('html') || type.includes('css')) return 'Code';
  
  return 'File';
}

/**
 * Truncate text to specified length
 */
export function truncateText(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Generate random color for avatars or placeholders
 */
export function generateColor(seed) {
  const colors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
  ];
  
  const hash = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    textArea.remove();
  }
}

/**
 * Generate a secure random password
 */
export function generatePassword(length = 12) {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  
  return password;
}

/**
 * Validate email address
 */
export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate URL
 */
export function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get share URL for a file
 */
export function getShareUrl(shareId) {
  return `${window.location.origin}/share/${shareId}`;
} 